"use client"

import { useState } from "react"
import { ChevronRight, Link2, Check, Shield, CreditCard } from "lucide-react"
import Navbar from "@/app/Components/NavbarSection"
import Footer from "@/app/Sections/Footer"

const sections = [
    {
        id: "guarantee",
        number: "01",
        title: "The 24-Hour Money-Back Guarantee",
        body: "We offer a full refund on eligible services requested within exactly 24 hours of the initial payment and service activation. The countdown begins the moment the service is 'Active' in our system. Refunds are only applicable to new customers or the first service purchased by an existing customer.",
    },
    {
        id: "eligible-services",
        number: "02",
        title: "Eligible Services",
        body: "Refunds under the 24-hour guarantee generally apply to Shared Web Hosting plans, Game Server Hosting (Minecraft, MTA, GTA V, etc.), and Standard VPS (Virtual Private Server) plans.",
    },
    {
        id: "non-refundable",
        number: "03",
        title: "Non-Refundable Items & Services",
        body: "Certain costs are immediate and non-recoverable. Non-refundable items include: Domain Names, Dedicated IP Addresses, Software Licenses (cPanel, Plesk, Windows Server, etc.), Dedicated Servers (due to manual labor and hardware provisioning costs), Add-ons (management, specialized backups, setup fees), and Cryptocurrency Payments.",
    },
    {
        id: "renewals",
        number: "04",
        title: "Service Renewals",
        body: "All renewals are final. This refund policy only applies to the initial purchase of a service. We recommend cancelling your service at least 48 hours before the renewal date if you do not wish to be billed for the next cycle.",
    },
    {
        id: "violations",
        number: "05",
        title: "Terms of Service (ToS) Violations",
        body: "If your service is suspended or terminated due to a violation of our Acceptable Use Policy, you forfeit all rights to a refund, regardless of when the request is made. We do not reward 'burn and churn' behavior that harms our network integrity.",
    },
    {
        id: "procedure",
        number: "06",
        title: "Refund Processing Procedure",
        body: "To request a refund, you must open a Billing Ticket through the Aurex Hosting Client Portal stating your reason. The request must be timestamped within the 24-hour eligibility window. Approved refunds are usually processed within 5–10 business days depending on your bank or payment processor.",
    },
    {
        id: "disputes",
        number: "07",
        title: "Chargebacks and Disputes",
        body: "Opening a dispute or 'chargeback' with your bank or payment processor before contacting our support team is a violation of these terms. This will result in the immediate and permanent termination of your account and all associated services, and your details may be reported to industry-standard fraud databases.",
    },
]

function CopyLinkButton({ id }: { id: string }) {
    const [copied, setCopied] = useState(false)
    const copy = () => {
        if (typeof window === "undefined") return
        navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}#${id}`)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    return (
        <button
            onClick={copy}
            aria-label="Copy section link"
            className="flex-shrink-0 p-1.5 rounded-md text-gray-600 hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
        >
            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Link2 className="w-3.5 h-3.5" />}
        </button>
    )
}

export default function RefundPolicyPage() {
    return (
        <div className="bg-black">
            <Navbar />
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: "url('https://cdn.aurex.lk/assets/hero-mc.webp')",
                            backgroundSize: "cover",
                            backgroundPosition: "center top",
                            filter: "blur(6px) brightness(0.45)",
                            transform: "scale(1.05)",
                        }}
                    />
                    <div className="absolute inset-0 bg-[#3b0d6e]/40" />
                    <div
                        className="absolute inset-0"
                        style={{ background: "radial-gradient(ellipse 90% 60% at 50% 110%, #000000 0%, transparent 90%)" }}
                    />
                    <div
                        className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
                        style={{ background: "linear-gradient(to bottom, #000000, transparent)" }}
                    />
                </div>

                <div className="relative z-10">
                    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-12 pb-24">
                        <nav className="flex items-center gap-1.5 text-xs font-poppins text-gray-500 mb-8">
                            <a href="/" className="hover:text-white transition-colors">Home</a>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-gray-400">Legal</span>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-white">Refund Policy</span>
                        </nav>
                        <h1 className="font-akira text-3xl sm:text-4xl xl:text-5xl text-white leading-tight tracking-wide mb-3">
                            Refund Policy
                        </h1>
                        <p className="text-gray-500 font-poppins text-sm">Effective Date: May 7, 2026</p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ lineHeight: 0 }}>
                        <svg width="100%" height="50" viewBox="0 0 2598 150" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMax meet" style={{ display: "block", position: "relative", zIndex: 20 }}>
                            <path d="M2598 150H0L230.359 0H2598V150Z" fill="black" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="relative z-10 w-full bg-black">
                <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 relative">

                    <div className="flex flex-wrap gap-3 mb-10">
                        <a href="/tos" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <Shield className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Terms of Use</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                        <a href="/privacy" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <Shield className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Privacy Policy</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                    </div>

                    <p className="text-gray-400 font-poppins text-sm leading-relaxed mb-10 border-l-4 border-[#8B5CF6] pl-4">
                        At Aurex Hosting, we strive to provide high-performance infrastructure. Because our resources (CPU, RAM, and NVMe storage) are allocated immediately upon purchase, we maintain a strict refund policy to ensure fairness and service stability.
                    </p>

                    <div className="flex flex-col">
                        {sections.map((s, i) => (
                            <div key={s.id}>
                                <section id={s.id} className="py-8">
                                    <div className="flex items-start gap-3 mb-4">
                                        <span className="font-akira text-[#8B5CF6]/50 text-xs mt-1 flex-shrink-0 select-none">{s.number}</span>
                                        <h2 className="font-akira text-white text-base sm:text-lg leading-snug flex-1">{s.title}</h2>
                                        <CopyLinkButton id={s.id} />
                                    </div>
                                    <p className="text-gray-400 font-poppins text-sm leading-relaxed pl-7">{s.body}</p>
                                </section>
                                {i < sections.length - 1 && (
                                    <hr className="border-white/5" />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 pt-8 border-t border-white/5">
                        <p className="text-gray-600 font-poppins text-xs">
                            Questions about refunds?{" "}
                            <a href="mailto:billing@aurex.lk" className="text-[#8B5CF6] hover:text-purple-400 transition-colors">
                                billing@aurex.lk
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    )
}
